
import { useState, useEffect } from "react";

export interface LoanCalculation {
  monthlyPayment: number;
  totalPaid: number;
  interestAndFees: number;
  interestRate: number;
  monthlyFee: number;
  setupFee: number;
}

interface LoanRates {
  interestRate: number;
  monthlyFee: number;
  setupFee: number;
}

const getLoanTypeRates = (loanType: string): LoanRates => {
  // Realistic interest rates and fees for different loan types
  switch (loanType) {
    case "asuntolaina":
      return {
        interestRate: 3.2,
        monthlyFee: 4,
        setupFee: 300,
      };
    case "autolaina":
      return {
        interestRate: 5.5,
        monthlyFee: 5,
        setupFee: 200,
      };
    case "venelaina":
      return {
        interestRate: 6.8,
        monthlyFee: 5.5,
        setupFee: 250,
      };
    case "remonttilaina":
      return {
        interestRate: 5.2,
        monthlyFee: 4.5,
        setupFee: 180,
      };
    case "haalaina":
      return {
        interestRate: 5.9,
        monthlyFee: 5,
        setupFee: 190,
      };
    case "yrityslaina":
      return {
        interestRate: 6.5,
        monthlyFee: 8,
        setupFee: 350,
      };
    case "joustoluotto":
      return {
        interestRate: 8.5,
        monthlyFee: 6,
        setupFee: 150,
      };
    case "kulutusluotto":
    default:
      return {
        interestRate: 6.9,
        monthlyFee: 5,
        setupFee: 150,
      };
  }
};

export const useLoanCalculator = (
  loanAmount: number,
  loanTerm: number,
  loanType: string = "kulutusluotto"
): LoanCalculation => {
  const [calculation, setCalculation] = useState<LoanCalculation>({
    monthlyPayment: 0,
    totalPaid: 0,
    interestAndFees: 0,
    interestRate: 0,
    monthlyFee: 0,
    setupFee: 0,
  });

  useEffect(() => {
    // Get rates and fees for the selected loan type
    const { interestRate, monthlyFee, setupFee } = getLoanTypeRates(loanType);

    // Calculate monthly payment
    const calculateMonthlyPayment = () => {
      const monthlyInterest = interestRate / 100 / 12;
      const totalPayments = loanTerm * 12;
      const x = Math.pow(1 + monthlyInterest, totalPayments);
      const basicMonthlyPayment = (loanAmount * x * monthlyInterest) / (x - 1);
      
      // Adding fees:
      const monthlyFeeComponent = monthlyFee;
      const setupFeeComponent = setupFee / totalPayments;
      
      const totalMonthlyPayment = basicMonthlyPayment + monthlyFeeComponent + setupFeeComponent;
      
      return isNaN(totalMonthlyPayment) ? 0 : Math.round(totalMonthlyPayment);
    };

    // Calculate total cost
    const calculateTotalCost = (monthlyPayment: number) => {
      const totalMonths = loanTerm * 12;
      const totalPaid = monthlyPayment * totalMonths;
      const interestAndFees = totalPaid - loanAmount;
      return { totalPaid, interestAndFees };
    };

    const monthlyPayment = calculateMonthlyPayment();
    const { totalPaid, interestAndFees } = calculateTotalCost(monthlyPayment);

    setCalculation({
      monthlyPayment,
      totalPaid,
      interestAndFees,
      interestRate,
      monthlyFee,
      setupFee,
    });
  }, [loanAmount, loanTerm, loanType]);

  return calculation;
};
